import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CalendarSyncResult {
  success: boolean;
  message: string;
  data: {
    totalInteractions: number;
    syncedEvents: number;
    createdEvents?: number;
    updatedEvents?: number;
    failedEvents: number;
    userEmail?: string;
    testMode?: boolean;
    results: Array<{
      interactionId: string;
      eventId?: string;
      status: 'success' | 'failed' | 'updated';
      action?: 'created' | 'updated' | 'failed';
      error?: string;
      testMode?: boolean;
    }>;
  };
}

export interface CalendarEvent {
  id: string;
  title?: string; // Added for compatibility
  summary: string;
  description: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  htmlLink: string;
  extendedProperties?: {
    private: {
      interactionId?: string;
      reservationId?: string;
      interactionStatus?: string;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private apiUrl = `${environment.apiUrl}/calendar`;

  constructor(private http: HttpClient) {}

  /**
   * Initiate Google Calendar OAuth2 authorization
   */
  initiateGoogleCalendarAuth(): Observable<{
    success: boolean;
    message: string;
    data: {
      authUrl: string;
      userId: string;
    };
  }> {
    return this.http.get<{
      success: boolean;
      message: string;
      data: {
        authUrl: string;
        userId: string;
      };
    }>(`${this.apiUrl.replace('/calendar', '/google-calendar')}/auth/initiate`);
  }

  /**
   * Handle Google Calendar OAuth2 callback
   */
  handleGoogleCalendarCallback(code: string): Observable<any> {
    return this.http.get(`${this.apiUrl.replace('/calendar', '/google-calendar')}/auth/callback?code=${code}`);
  }

  /**
   * Sync all user interactions to Google Calendar (OAuth2 version)
   */
  syncInteractionsToGoogleCalendar(userId: string): Observable<CalendarSyncResult> {
    return this.http.post<CalendarSyncResult>(`${this.apiUrl.replace('/calendar', '/google-calendar')}/sync/${userId}`, {});
  }

  /**
   * Create a single event from an interaction
   */
  createEventFromInteraction(interactionId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/event/${interactionId}`, {});
  }

  /**
   * List Google Calendar events
   */
  listGoogleCalendarEvents(maxResults: number = 10): Observable<{
    success: boolean;
    message: string;
    data: {
      events: CalendarEvent[];
      totalEvents: number;
    };
  }> {
    return this.http.get<{
      success: boolean;
      message: string;
      data: {
        events: CalendarEvent[];
        totalEvents: number;
      };
    }>(`${this.apiUrl}/events?maxResults=${maxResults}`);
  }

  /**
   * Delete a Google Calendar event
   */
  deleteGoogleCalendarEvent(eventId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/event/${eventId}`);
  }

  /**
   * Initiate Outlook Calendar OAuth2 authorization
   */
  initiateOutlookCalendarAuth(): Observable<{
    success: boolean;
    message: string;
    data: {
      authUrl: string;
      userId: string;
    };
  }> {
    return this.http.get<{
      success: boolean;
      message: string;
      data: {
        authUrl: string;
        userId: string;
      };
    }>(`${this.apiUrl.replace('/calendar', '/outlook-calendar')}/auth/initiate`);
  }

  /**
   * Handle Outlook Calendar OAuth2 callback
   */
  handleOutlookCalendarCallback(code: string): Observable<any> {
    return this.http.get(`${this.apiUrl.replace('/calendar', '/outlook-calendar')}/auth/callback?code=${code}`);
  }

  /**
   * Sync all user interactions to Outlook Calendar (OAuth2 version)
   */
  syncInteractionsToOutlookCalendar(userId: string): Observable<CalendarSyncResult> {
    return this.http.post<CalendarSyncResult>(`${this.apiUrl.replace('/calendar', '/outlook-calendar')}/sync/${userId}`, {});
  }

  /**
   * List Outlook Calendar events
   */
  listOutlookCalendarEvents(maxResults: number = 10): Observable<{
    success: boolean;
    message: string;
    data: {
      events: CalendarEvent[];
      totalEvents: number;
    };
  }> {
    return this.http.get<{
      success: boolean;
      message: string;
      data: {
        events: CalendarEvent[];
        totalEvents: number;
      };
    }>(`${this.apiUrl.replace('/calendar', '/outlook-calendar')}/events?maxResults=${maxResults}`);
  }

}
